/* Core & libraries */
import React, { useCallback, useState } from 'react'
import Dropzone from 'react-dropzone-uploader-with-credentials'
import { useTheme } from "react-jss";
import Row from "react-bootstrap/Row";
import { FormattedMessage, useIntl } from "react-intl";
import Col from "react-bootstrap/Col";

/* Helpers, constants, custom methods, internal libraries */
import { trackEvent } from "../../services/mixpanelUtil";
import { useNotificationContext } from "../../contexts/notificationContext";
import { useSpinnersUpdateContext } from '../../contexts/spinnersContext';
import { useUserContext } from "../../contexts/userContext";
import { getCookieValue } from "../../helpers/general";
import { UPLOAD_PEOPLE_ENDPOINT } from "../../endpoints/endpoints";
import { AIRLINES, API_HOST, API_PROTOCOL } from "../../constants/appConstants";
/* Components */
import UCButton from "../buttons";
import CustomPreview from "./customPreview/customPreview";
import CustomInput from "./customInput/customInput";
import CustomSubmitButton from "./customSubmitButton/customSubmitButton";

/* Styles */
import styles from "./styles";

const UCDropzone = ({ setShowModal, isUploading, setIsUploading, getPendingInviteCount, providerCode }) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const intl = useIntl();

  const { setNotification } = useNotificationContext();
  const setSpinner = useSpinnersUpdateContext();

  const userContext = useUserContext();

  const [emailColumnPresent, setEmailColumnPresent] = useState(false);
  const [correctNumberOfCommas, setCorrectNumberOfCommas] = useState(true);

  // specify upload params and url for your files
  const getUploadParams = ({ meta, file }) => {
    const body = new FormData()
    body.append('file', file)
    body.append('companyEntityID', userContext?.userInfo?.companyInfo?.companyEntityId)
    return {
      url: API_PROTOCOL + "://" + API_HOST + UPLOAD_PEOPLE_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()),
      body,
      headers: {
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      }
    }
  }

  // called every time a file's `status` changes
  const handleChangeStatus = ({ meta, file, xhr }, status) => {
    if (status === "uploading") {
      setSpinner({
        ["uploadFileSpinner"]: true
      })
      trackEvent('Upload File for Invites - Start', {});
      setIsUploading(true);
    } else if (["error", "headers_received", "exception", "error_upload"].includes(status)) {
      setIsUploading(false);
    }

    if (['exception_upload', 'error_upload'].includes(status)) {
      xhr.onload = function () {
        if(xhr?.status === 403) {
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadPeople.errorTitle" />,
            <FormattedMessage id="notifications.uploadPeople.403" />          
          );
        } else if (xhr?.status === 500) {
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadPeople.errorTitle" />,
            <FormattedMessage id="notifications.uploadPeople.403" />          
          );
        } else if(xhr?.status === 400) {
          if(JSON.parse(xhr?.response)?.errorCode === "7001")
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadPeople.errorTitle" />,
            <FormattedMessage id="notifications.uploadPeople.7001" />
          );
          if(JSON.parse(xhr?.response)?.errorCode === "7002")
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadPeople.errorTitle" />,
            <FormattedMessage id="notifications.uploadPeople.7002" />          
          );
          if(JSON.parse(xhr?.response)?.errorCode === "7003")
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadPeople.errorTitle" />,
            <FormattedMessage id="notifications.uploadPeople.7003" />
          );
          if(JSON.parse(xhr?.response)?.errorCode === "7004")
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadPeople.errorTitle" />,
            <FormattedMessage id="notifications.uploadPeople.7004" />
          );
          if(JSON.parse(xhr?.response)?.errorCode === "7005")
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadPeople.errorTitle" />,
            <FormattedMessage id="notifications.uploadPeople.7005" />
          );
          if(JSON.parse(xhr?.response)?.errorCode === "7006")
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadPeople.errorTitle" />,
            <FormattedMessage id="notifications.uploadPeople.7006" />
          );
        } else {
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadPeople.errorTitle" />,
            xhr?.response
          );
        }
      };
      setSpinner({
        ["uploadFileSpinner"]: false
      })
    }
    let trackingProps = {
      'Status': '',
      'Number of Invites Sent': 0,
      'File Error': '',
      'Upload Error': ''
    };

    if (status === "preparing") {
      return new Promise((resolve) => {
        const regexp = /^[a-zA-Z0-9-_. ()]+$/;
        if (file.name.search(regexp) === -1) {
          resolve(intl.formatMessage({ id: "dropzone.invalidName" }));
        } else {
          let reader = new FileReader();
          reader.onloadend = () => {
            let lines = reader.result.split('\n').map(line => line.trim()).filter(Boolean);
            if (lines.length > 1) {
              let columns = lines[0].split(',').map(col => col.trim().toLowerCase());
              setEmailColumnPresent(columns.includes("email"))
              let numberOfCommas = lines[0].split(',').length - 1;
              meta.emailColumnPresent = emailColumnPresent; // <-- Add this line
              meta.correctNumberOfCommas = correctNumberOfCommas
              for (let i = 1; i < lines.length; i++) {
                if (lines[i].split(',').length - 1 !== numberOfCommas) {
                  setCorrectNumberOfCommas(false)
                  return;
                } else {
                  setCorrectNumberOfCommas(true)
                  return
                }
              }
              meta.numberOfInvites = lines.length - 1;
              resolve(false); // No error, file is valid.
            }
          };
          let blob = new Blob([file], { type: file.type });
          reader.readAsText(blob);
        }
      }).then((errorMessage) => {
        if (errorMessage) {
          setSpinner({
            ["uploadFileSpinner"]: false
          })
          trackingProps['Status'] = 'Error';
          trackingProps['File Error'] = errorMessage;

          trackEvent('Upload File for Invites - Completion', trackingProps);

          setIsUploading(false);
          setNotification(
            "error",
            <FormattedMessage id="notifications.uploadPeople.errorTitle" />,
            errorMessage
          );
          return;
        }
      });
    } else if (status === "done") {
      setSpinner({
        ["uploadFileSpinner"]: false
      })
      trackingProps['Status'] = 'Successful';
      trackingProps['Number of Invites Sent'] = meta.numberOfInvites || 0;

      trackEvent('Upload File for Invites - Completion', trackingProps);

      getPendingInviteCount();
      setIsUploading(false);
      setNotification(
        "success",
        <FormattedMessage id="notifications.uploadPeople.successTitle" />,
        <FormattedMessage id="notifications.uploadPeople.successText" />
      );
      setShowModal(false);
    } else if (status === "error") {
      trackingProps['Status'] = 'Error';
      trackingProps['Upload Error'] = 'Upload Error';

      trackEvent('Upload File for Invites - Completion', trackingProps);
    }
}

  // receives array of files that are done uploading when submit button is clicked
  const handleSubmit = (files, allFiles) => {
    allFiles.forEach(f => f.restart())
  }

  const getEmailDesc = () => {
    switch (providerCode) {
      case AIRLINES.CHOICE:
        return (
          <div>

          </div>
        )
      default:
        return (
          <div>
            <FormattedMessage id="peopleHeader.modalUpload.emailDescription" />
          </div>
        )
    }
  }

  const getTeamDesc = () => {
    switch (providerCode) {
      case AIRLINES.CHOICE:
        return (
          <div>
            <FormattedMessage id="peopleHeader.modalUpload.teamDescriptionCHOICE" />
          </div>
        )
      default:
        return (
          <div>
            <FormattedMessage id="peopleHeader.modalUpload.teamDescription" />
          </div>
        )
    }
  }

  const customLayout = ({ input, previews, submitButton, dropzoneProps, files, extra: { maxFiles } }) => {
    return (
      <div>
        {previews}
        {
          files.length < maxFiles &&
          <div {...dropzoneProps}>
            {input}
          </div>
        }
        <p className={classes.details}>
          <FormattedMessage id="peopleHeader.modalUpload.details" />
        </p>
        <p className={classes.optionDetails}>
          <span className={classes.element}>email</span>
          { getEmailDesc() }
        </p>
        <p className={classes.optionDetails}>
          <span className={classes.element}>team</span>
          { getTeamDesc() }
        </p>
        <Row className={classes.buttonsWrapper}>
          <Col className={classes.colCancel}>
            <UCButton
              onClick={() => setShowModal(false)}
              type="default"
              className={classes.button}
              text={<FormattedMessage id="peopleHeader.modalUpload.cancel" />}
              disabled={isUploading}
            />
          </Col>
          <Col className={classes.colSubmit}>
            {submitButton}
          </Col>
        </Row>
      </div>
    )
  }

  const maxSize = 5242880;
  //const minSize = 15;

  return (
    <Dropzone
      autoUpload={false}
      onChangeStatus={handleChangeStatus}
      getUploadParams={getUploadParams}
      LayoutComponent={customLayout}
      onSubmit={handleSubmit}
      maxFiles={1}
      accept=".csv"
      validate={(file) => {
        const regexp = /^[a-zA-Z0-9-_. ()]+$/;
        if (file.file.name.search(regexp) === -1) {
          return intl.formatMessage({ id: "dropzone.invalidName" });
        }
      }}
      maxSizeBytes={maxSize}
      //minSizeBytes={minSize}
      classNames={{ dropzone: classes.dropzone }}
      PreviewComponent={props => <CustomPreview {...props} correctNumberOfCommas={correctNumberOfCommas} emailColumnPresent={emailColumnPresent} setIsUploading={setIsUploading}/>}
      InputComponent={props => <CustomInput {...props} providerCode={providerCode}/>}
      SubmitButtonComponent={props => <CustomSubmitButton {...props} correctNumberOfCommas={correctNumberOfCommas} emailColumnPresent={emailColumnPresent} buttonClassName={classes.button} />}
    />
  )
}

export default UCDropzone;